
import React, { createContext, useContext, useReducer, useEffect, useRef, useState } from 'react';
import { AuthState, User } from '@/types/auth';
import { authReducer, initialState } from './auth/auth-reducer';
import { useFetchProfile } from '@/hooks/use-fetch-profile';
import { useAuthSession } from '@/hooks/use-auth-session';
import { useAuthInitialization } from '@/hooks/use-auth-initialization';
import { useAuthSubscription } from '@/hooks/use-auth-subscription';
import { clearCachedProfile } from '@/utils/profile-cache';
import { toast } from 'sonner';
import { Session } from '@supabase/supabase-js';
import { mobileDebugger } from '@/utils/mobileDebugger';
import { supabase } from '@/integrations/supabase/client';
import { getLogger } from '@/utils/logger';

const log = getLogger({ module: 'AuthContext' });

// Development mode is disabled to ensure consistent behavior with production
const LOCAL_DEV_MODE = false;

const AuthContext = createContext<AuthState & {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<Session | null>;
  refreshProfile: () => Promise<void>;
  isLocalDevMode?: boolean;
}>({
  user: null,
  profile: null,
  isLoading: true,
  isInitialized: false,
  authError: null,
  login: async () => {},
  logout: async () => {},
  refreshSession: async () => null,
  refreshProfile: async () => {},
  isLocalDevMode: LOCAL_DEV_MODE,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  // Track if user has manually logged out to prevent auto-login in dev mode
  const [manuallyLoggedOut, setManuallyLoggedOut] = useState(false);
  const fetchProfile = useFetchProfile(dispatch);
  const authInProgressRef = useRef(false);
  const activeProfileFetchRef = useRef(false);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  const { refreshSession: refreshSessionFn, login: loginFn, logout: logoutFn } = useAuthSession();
  const { initCompletedRef } = useAuthInitialization(fetchProfile, dispatch);
  const { cleanupSubscription } = useAuthSubscription(fetchProfile, dispatch, state.user);

  useEffect(() => {
    if (isMobile) {
      mobileDebugger.logAuthEvent('init_start', { 
        providerMounted: true,
        initStartTime: Date.now()
      });
    }
  }, []);
  
  // Local dev mode - automatically provide a mock user for local development
  useEffect(() => {
    if (LOCAL_DEV_MODE && !state.isInitialized && !manuallyLoggedOut) {
      console.info('🔧 FOMO Local Development Mode: Bypassing authentication');
      
      // Create mock user and profile for local development
      const mockUser = {
        id: 'local-dev-user-123',
        email: 'local-dev@example.com',
        username: 'LocalDev'
      };
      
      const mockProfile = {
        id: 'local-dev-profile-123',
        user_id: mockUser.id,
        username: 'LocalDev',
        display_name: 'Local Developer',
        bio: 'Working on Canvas feature locally',
        profile_image_url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=LocalDev',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        age_verified_at: new Date().toISOString(),
        mature_content_enabled: true,
        is_banned: false
      };

      // Set mock user state
      dispatch({ type: 'SET_USER', payload: mockUser });
      dispatch({ type: 'SET_PROFILE', payload: mockProfile });
      dispatch({ type: 'SET_INITIALIZED', payload: true });
      dispatch({ type: 'SET_LOADING', payload: false });
      
      console.info('🔧 FOMO Local Development Mode: Mock user created', { mockUser, mockProfile });
    }
  }, []);

  useEffect(() => {
    if (isMobile && initCompletedRef.current && state.isInitialized) {
      mobileDebugger.logAuthEvent('init_complete', {
        hasUser: !!state.user,
        hasError: !!state.authError,
        isLoading: state.isLoading,
        initCompletionTime: Date.now()
      });
    }
  }, [state.isInitialized, state.user, state.authError, state.isLoading]);

  const login = async () => {
    if (authInProgressRef.current) {
      console.log('Login already in progress, ignoring duplicate call');
      return;
    }
    
    // Reset manual logout flag to allow login
    setManuallyLoggedOut(false);
    
    authInProgressRef.current = true;
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const session = await loginFn();
      
      if (!session) {
        console.log('No active session, user needs to authenticate');
      } else {
        toast.success('Login successful');
      }
    } catch (error) {
      console.error('Error during login:', error);
      dispatch({ type: 'SET_ERROR', payload: error as Error });
      dispatch({ type: 'SET_LOADING', payload: false });
      toast.error('Login failed. Please try again.');
      
      if (isMobile) {
        mobileDebugger.logError(error, 'login_error');
      }
      
      throw error;
    } finally {
      authInProgressRef.current = false;
    }
  };

  const logout = async () => {
    if (authInProgressRef.current) {
      console.log('Logout already in progress, ignoring duplicate call');
      return;
    }
    
    authInProgressRef.current = true;
    
    // Set timeout to force-reset the lock if logout hangs
    const timeoutId = setTimeout(() => {
      console.warn('Logout timeout - forcing ref reset');
      authInProgressRef.current = false;
      dispatch({ type: 'SET_LOADING', payload: false });
    }, 30000); // 30 second timeout
    
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      cleanupSubscription();
      
      const userId = state.user?.id;
      
      await logoutFn();
      
      if (userId) {
        clearCachedProfile(userId);
      }
      
      // Mark that we've manually logged out to prevent auto-login in dev mode
      setManuallyLoggedOut(true);
      
      // Disable maintenance mode on logout to prevent redirect loop
      localStorage.setItem('maintenance_mode', 'false');
      
      dispatch({ type: 'RESET' });
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      dispatch({ type: 'SET_ERROR', payload: error as Error });
      toast.error('Logout failed. Please try again.');
      
      if (isMobile) {
        mobileDebugger.logError(error, 'logout_error');
      }
      
      throw error;
    } finally {
      clearTimeout(timeoutId);
      dispatch({ type: 'SET_LOADING', payload: false });
      authInProgressRef.current = false;
    }
  };

  const refreshSession = async (): Promise<Session | null> => {
    try {
      if (isMobile) {
        mobileDebugger.logAuthEvent('refresh_start', { 
          calledFrom: 'refreshSession_function',
          hasUser: !!state.user
        });
      }
      
      const session = await refreshSessionFn();
      
      if (isMobile) {
        mobileDebugger.logAuthEvent('refresh_complete', { 
          success: !!session,
          sessionExpiry: session?.expires_at
        });
      }
      
      if (!session && state.user) {
        log.warn('Session missing but user state exists - preserving state for potential reconnection');
        // Don't automatically reset state in case of temporary connection issues
      }
      
      return session;
    } catch (error) {
      console.error('Error in refreshSession:', error);
      dispatch({ type: 'SET_ERROR', payload: error as Error });
      
      if (isMobile) {
        mobileDebugger.logError(error, 'refresh_session_error', {
          hasUser: !!state.user,
          isInitialized: state.isInitialized,
          isLoading: state.isLoading
        });
      }
      
      throw error;
    }
  };

  // Manual profile refresh method
  const refreshProfile = async () => {
    if (!state.user?.id) {
      log.warn('Cannot refresh profile - no user ID');
      return;
    }

    try {
      log.info('Manually refreshing profile for user:', state.user.id);
      // Clear cache first to ensure fresh data
      clearCachedProfile(state.user.id);
      // Skip cache when fetching to get the latest from database
      const profile = await fetchProfile(state.user.id, 0, true);
      
      if (profile) {
        log.info('Profile refreshed successfully:', {
          username: profile.username,
          displayName: profile.display_name,
          hasCompletedOnboarding: profile.has_completed_onboarding,
          profileImageUrl: profile.profile_image_url
        });
      } else {
        log.warn('No profile found after refresh');
      }
    } catch (error) {
      log.error('Error refreshing profile:', error);
      throw error;
    }
  };

  useEffect(() => {
    console.log('Auth context state changed:', {
      hasUser: !!state.user,
      userId: state.user?.id,
      hasProfile: !!state.profile,
      isLoading: state.isLoading,
      isInitialized: state.isInitialized,
      hasError: !!state.authError,
    });
  }, [state]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        refreshSession,
        refreshProfile,
        isLocalDevMode: LOCAL_DEV_MODE
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
